// src/lib/applicationDocs.ts: storage for generated contracts and invoices.
//
// The 'documents' bucket is private. Everything is reached through signed URLs:
// long-lived ones are stored on the application row so the emailed links keep
// working, and the portal mints a fresh short-lived one each time it loads.
import { supabase } from '../services/supabase';
import { htmlToBlob } from './generatePDF';

export const DOCUMENTS_BUCKET = 'documents';

export type DocKind = 'contract' | 'invoice' | 'contract-signed';

/** applications/{id}/contract.html, the folder name is the application id, which
 *  is what the storage RLS policies match on. */
export const docPath = (applicationId: string, kind: DocKind) =>
  `applications/${applicationId}/${kind}.html`;

/** A year, so an emailed link outlives the signing window. Supabase caps signed
 *  URLs at the project's JWT lifetime, but a year is accepted in practice. */
export const EMAIL_URL_TTL = 60 * 60 * 24 * 365;

/** Short-lived, for a link the portal has just minted and is about to open. */
export const VIEW_URL_TTL = 60 * 60;

export async function uploadDoc(applicationId: string, kind: DocKind, html: string): Promise<string> {
  const path = docPath(applicationId, kind);
  const { error } = await supabase.storage
    .from(DOCUMENTS_BUCKET)
    .upload(path, htmlToBlob(html), { upsert: true, contentType: 'text/html;charset=utf-8' });
  if (error) throw error;
  return path;
}

export async function signedDocUrl(path: string, expiresIn = VIEW_URL_TTL): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(DOCUMENTS_BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error) return null;
  return data?.signedUrl || null;
}

/** Upload a document and return a signed URL for it in one step. */
export async function uploadAndSign(
  applicationId: string, kind: DocKind, html: string, expiresIn = EMAIL_URL_TTL,
): Promise<{ path: string; url: string | null }> {
  const path = await uploadDoc(applicationId, kind, html);
  return { path, url: await signedDocUrl(path, expiresIn) };
}

/**
 * Read a stored document back as HTML text, for rendering in DocumentModal.
 *
 * The contract and invoice are regenerated from the application row instead of
 * being read back, but a countersigned agreement carries the drawn signature
 * image, which exists only inside the stored file, so that one has to be
 * fetched.
 */
export async function fetchDocHtml(applicationId: string, kind: DocKind): Promise<string | null> {
  const url = await signedDocUrl(docPath(applicationId, kind));
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    // The bucket stores these as text/html;charset=utf-8, so text() decodes
    // them as UTF-8 rather than guessing.
    return await res.text();
  } catch {
    return null;
  }
}

/**
 * A stored signed URL eventually expires. Prefer a freshly minted one and fall
 * back to whatever was saved, so the portal keeps working either way.
 */
export async function freshOrStoredUrl(
  applicationId: string, kind: DocKind, stored: string | null,
): Promise<string | null> {
  return (await signedDocUrl(docPath(applicationId, kind))) || stored;
}
